/**
 * Role gates for assist anchors. Registry entries name the lowest role that
 * can use the control (`minRole`); the manifest expands that to the explicit
 * list of tenant roles, stored lowercase exactly as the backend stores them,
 * so the backend can filter with a plain membership check.
 *
 * Hierarchy (authSlice `selectCanEdit` docs): viewer < editor <
 * administrator < owner < global_super_admin.
 *
 * No imports: the Node manifest build (`scripts/build-assist-manifest.mjs`)
 * loads this module directly.
 */

/** Every tenant role, lowest first. */
export const TENANT_ROLES = Object.freeze(['viewer', 'editor', 'administrator', 'owner', 'global_super_admin'])

/**
 * `minRole` values an entry may use, mapped to the first role in
 * `TENANT_ROLES` they admit. `admin` mirrors `selectCanManage`, `owner`
 * mirrors `selectCanManageBilling`.
 */
export const MIN_ROLE_FLOOR = Object.freeze({
  viewer: 'viewer',
  editor: 'editor',
  admin: 'administrator',
  owner: 'owner',
})

/**
 * @param {keyof typeof MIN_ROLE_FLOOR} minRole
 * @returns {string[]} The tenant roles at or above `minRole`.
 * @throws {Error} for an unknown `minRole`.
 */
export function rolesAtOrAbove(minRole) {
  const floor = MIN_ROLE_FLOOR[minRole]
  if (!floor) throw new Error(`assist: unknown minRole "${minRole}"`)
  return TENANT_ROLES.slice(TENANT_ROLES.indexOf(floor))
}
